import { useCallback } from 'react';
import { Dispatch, SetStateAction } from 'react';
import { FormattedAudio } from 'types/kontentAi';
import { selectAudioSource } from 'utils/mediaUtils';

type AudioType = 'bridge' | 'response' | 'step';

interface AudioSetters {
  setCurrentAudioSource?: Dispatch<SetStateAction<string>>;
  setCurrentAudioType?: Dispatch<SetStateAction<AudioType>>;
  setCurrentAudioCaptions?: Dispatch<SetStateAction<string>>;
}

/**
 * Returns a `playAudio` function that sets the current audio source, type, and
 * captions. Returns `true` if audio was played, `false` if setters are missing
 * or the audio has no resolvable URL (useful for falling back to `onContinue`).
 */
export const usePlayAudio = ({
  setCurrentAudioSource,
  setCurrentAudioType,
  setCurrentAudioCaptions,
}: AudioSetters) => {
  return useCallback(
    (
      audio: FormattedAudio | null | undefined,
      type: AudioType = 'response'
    ): boolean => {
      if (
        !setCurrentAudioSource ||
        !setCurrentAudioType ||
        !setCurrentAudioCaptions
      ) {
        return false;
      }

      const url = selectAudioSource(audio ?? undefined);
      if (!url) {
        return false;
      }

      setCurrentAudioSource(url);
      setCurrentAudioType(type);
      setCurrentAudioCaptions(audio?.closedCaption?.url || '');
      return true;
    },
    [setCurrentAudioSource, setCurrentAudioType, setCurrentAudioCaptions]
  );
};
